import React, { useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Chip,
  Stack,
  Avatar,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Breadcrumbs,
  Link,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search,
  CalendarToday,
  ArrowForward,
  Tag,
  TrendingUp,
  AccessTime,
  Home,
  NavigateNext,
  Visibility,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { blogPosts, authors, getAllTags, getBlogPostsByTag } from '../data/blogPosts';

const BlogPage = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');

  const allTags = getAllTags();
  
  const filteredPosts = React.useMemo(() => {
    let posts = [...blogPosts].sort((a, b) => b.date - a.date);
    
    if (selectedTag !== 'all') {
      posts = getBlogPostsByTag(selectedTag);
    }
    
    if (searchTerm) {
      posts = posts.filter(post => 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    return posts;
  }, [searchTerm, selectedTag]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.split(' ').length;
    return Math.ceil(words / wordsPerMinute);
  };

  const featuredPost = filteredPosts[0];
  const regularPosts = filteredPosts.slice(1);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNext fontSize="small" />} 
        sx={{ mb: 4 }}
      >
        <Link
          component={RouterLink}
          to="/"
          sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', textDecoration: 'none' }}
        >
          <Home sx={{ mr: 0.5, fontSize: 18 }} />
          Home
        </Link>
        <Typography color="text.primary" sx={{ fontWeight: 500 }}>
          Blog
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h2" component="h1" sx={{ mb: 2, fontWeight: 700 }}>
          PWGen-Rust Blog
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4, maxWidth: 600, mx: 'auto' }}>
          Security insights, development updates, and community highlights from the PWGen-Rust team
        </Typography>
        
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 4 }}>
          <Chip label="Security" color="primary" />
          <Chip label="Password Management" color="primary" variant="outlined" />
          <Chip label="Rust Development" color="secondary" />
          <Chip label="Open Source" color="secondary" variant="outlined" />
        </Stack>
      </Box>

      <Grid container spacing={4}>
        {/* Main Content */}
        <Grid size={{ xs: 12, lg: 8 }}>
          {/* Search and Filters */}
          <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Tag sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Filter:
                  </Typography>
                  <Chip
                    label="All"
                    onClick={() => setSelectedTag('all')}
                    color={selectedTag === 'all' ? 'primary' : 'default'}
                    variant={selectedTag === 'all' ? 'filled' : 'outlined'}
                    size="small"
                  />
                  {allTags.slice(0, 3).map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onClick={() => setSelectedTag(tag)}
                      color={selectedTag === tag ? 'primary' : 'default'}
                      variant={selectedTag === tag ? 'filled' : 'outlined'}
                      size="small"
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Featured Post */}
          {featuredPost && (
            <Card
              elevation={0}
              sx={{
                mb: 6,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2,
                },
              }}
            >
              <Grid container sx={{ height: { xs: 'auto', md: 400 } }}>
                <Grid size={{ xs: 12, md: 6 }} sx={{ position: 'relative', height: { xs: 300, md: '100%' } }}>
                  <CardMedia
                    component={RouterLink}
                    to={`/blog/${featuredPost.slug}`}
                    sx={{
                      display: 'block',
                      height: '100%',
                      width: '100%',
                      overflow: 'hidden',
                      textDecoration: 'none',
                      '& img': {
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                      },
                      '&:hover img': {
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <img
                      src={featuredPost.ogImage || '/img/pwgen-social-card.png'}
                      alt={featuredPost.title}
                    />
                  </CardMedia>
                  <Chip
                    label="Latest"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      fontWeight: 600,
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(0, 0, 0, 0.8)' 
                        : 'rgba(255, 255, 255, 0.9)',
                      color: theme.palette.mode === 'dark'
                        ? theme.palette.primary.contrastText
                        : theme.palette.primary.main,
                      backdropFilter: 'blur(8px)',
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                  <CardContent sx={{ p: { xs: 3, sm: 4 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(featuredPost.date)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {getReadingTime(featuredPost.content)} min read
                        </Typography>
                      </Stack>
                    </Stack>

                    <Typography
                      variant="h4"
                      component={RouterLink}
                      to={`/blog/${featuredPost.slug}`}
                      sx={{
                        mb: 2,
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: 'text.primary',
                        textDecoration: 'none',
                        display: 'block',
                        transition: 'color 0.2s ease',
                        '&:hover': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {featuredPost.title}
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
                      {featuredPost.excerpt}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                      {featuredPost.tags.slice(0, 3).map((tag, index) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          color={index % 2 === 0 ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                      ))}
                    </Stack>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {featuredPost.authors.map((authorId) => {
                          const author = authors[authorId];
                          return (
                            <Stack key={authorId} direction="row" spacing={1} alignItems="center">
                              <Avatar src={author.image} alt={author.name} sx={{ width: 32, height: 32 }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {author.name}
                                </Typography>
                              </Box>
                            </Stack>
                          );
                        })}
                      </Box>
                      <Button
                        component={RouterLink}
                        to={`/blog/${featuredPost.slug}`}
                        variant="contained"
                        endIcon={<ArrowForward />}
                      >
                        Read More
                      </Button>
                    </Box>
                  </CardContent>
                </Grid>
              </Grid>
            </Card>
          )}

          {/* Regular Posts Grid */}
          <Grid container spacing={3}>
            {regularPosts.map((post) => (
              <Grid size={{ xs: 12, md: 6 }} key={post.slug}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                    },
                  }}
                >
                  <CardMedia
                    component={RouterLink}
                    to={`/blog/${post.slug}`}
                    sx={{
                      display: 'block',
                      height: 160,
                      width: '100%',
                      overflow: 'hidden',
                      textDecoration: 'none',
                      '& img': {
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                      },
                      '&:hover img': {
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <img
                      src={post.ogImage || '/img/pwgen-social-card.png'}
                      alt={post.title}
                    />
                  </CardMedia>
                  
                  <CardContent sx={{ p: 3, height: 'calc(100% - 160px)', display: 'flex', flexDirection: 'column' }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarToday sx={{ fontSize: 14 }} />
                        {formatDate(post.date)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTime sx={{ fontSize: 14 }} />
                        {getReadingTime(post.content)} min
                      </Typography>
                    </Stack>

                    <Typography
                      variant="h6"
                      component={RouterLink}
                      to={`/blog/${post.slug}`}
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        color: 'text.primary',
                        textDecoration: 'none',
                        display: 'block',
                        transition: 'color 0.2s ease',
                        '&:hover': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {post.title}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.5, flexGrow: 1 }}>
                      {post.excerpt}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                      {post.tags.slice(0, 2).map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Stack>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={authors[post.authors[0]].image} sx={{ width: 24, height: 24 }} />
                        <Typography variant="caption" color="text.secondary">
                          {authors[post.authors[0]].name}
                        </Typography>
                      </Box>
                      <Button
                        component={RouterLink}
                        to={`/blog/${post.slug}`}
                        size="small"
                        endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                      >
                        Read
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* No Results */}
          {filteredPosts.length === 0 && (
            <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                No articles found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Try adjusting your search terms or filters
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedTag('all');
                }}
              >
                Clear Filters
              </Button>
            </Paper>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, lg: 4 }}>
          {/* Popular Tags */}
          <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Popular Topics
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {allTags.map((tag, index) => (
                <Chip
                  key={tag}
                  label={tag}
                  onClick={() => setSelectedTag(tag)}
                  color={selectedTag === tag ? 'primary' : 'default'}
                  variant={selectedTag === tag ? 'filled' : 'outlined'}
                  size="small"
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: selectedTag === tag ? undefined : 'action.hover' 
                    } 
                  }}
                />
              ))}
            </Box>
          </Paper>

          {/* Recent Posts */}
          <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Recent Articles
            </Typography>
            <List disablePadding>
              {blogPosts.slice(0, 5).map((post, index) => (
                <React.Fragment key={post.slug}>
                  <ListItem disablePadding>
                    <ListItemButton
                      component={RouterLink}
                      to={`/blog/${post.slug}`}
                      sx={{ px: 0, py: 1.5, borderRadius: 1 }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                            {post.title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(post.date)} • {getReadingTime(post.content)} min read
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < 4 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>

          {/* Newsletter Signup */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              border: '1px solid', 
              borderColor: 'primary.main',
              background: theme => theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(66, 165, 245, 0.05))'
                : 'linear-gradient(135deg, rgba(25, 118, 210, 0.05), rgba(66, 165, 245, 0.02))'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Stay Updated
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              Get notified about new PWGen-Rust releases, security insights, and development updates.
            </Typography>
            <Stack spacing={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                href="https://github.com/KiefStudioMA/PWGen-Rust/releases"
                target="_blank"
                startIcon={<TrendingUp />}
              >
                Follow Releases
              </Button>
              <Button
                variant="outlined"
                fullWidth
                href="https://github.com/KiefStudioMA/PWGen-Rust"
                target="_blank"
                startIcon={<Visibility />}
              >
                Watch on GitHub
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default BlogPage;